import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from '../hooks/use-toast';
import { 
  GitCompare, Plus, Trash2, GraduationCap, DollarSign, Users,
  Award, Loader2, CheckCircle, XCircle, ExternalLink, Save, Search, X
} from 'lucide-react';
import api, { collegesAPI } from '../utils/api';

const CollegeCompare = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [colleges, setColleges] = useState([]);
  const [allColleges, setAllColleges] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [savedComparisons, setSavedComparisons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedColleges, setSelectedColleges] = useState([]);
  const [showSelector, setShowSelector] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const collegeIds = searchParams.get('colleges');
    if (collegeIds) {
      const ids = collegeIds.split(',');
      setSelectedColleges(ids);
      loadComparison(ids);
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      const [collegesRes, savedRes] = await Promise.all([
        collegesAPI.getAll(),
        api.get('/compare/saved')
      ]);
      setAllColleges(collegesRes);
      setSavedComparisons(savedRes.data.comparisons || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadComparison = async (collegeIds) => {
    if (collegeIds.length < 2) {
      setComparison(null);
      return;
    }
    try {
      const res = await api.post('/compare/colleges', { college_ids: collegeIds });
      setComparison(res.data);
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to compare', variant: 'destructive' });
    }
  };

  const handleAddCollege = (collegeId) => {
    if (selectedColleges.length >= 5) {
      toast({ title: 'Limit Reached', description: 'Maximum 5 colleges can be compared', variant: 'destructive' });
      return;
    }
    if (selectedColleges.includes(collegeId)) return;
    
    const newSelection = [...selectedColleges, collegeId];
    setSelectedColleges(newSelection);
    navigate(`/compare?colleges=${newSelection.join(',')}`);
    setShowSelector(false);
    loadComparison(newSelection);
  };

  const handleRemoveCollege = (collegeId) => {
    const newSelection = selectedColleges.filter(id => id !== collegeId);
    setSelectedColleges(newSelection);
    if (newSelection.length > 0) {
      navigate(`/compare?colleges=${newSelection.join(',')}`);
      loadComparison(newSelection);
    } else {
      navigate('/compare');
      setComparison(null);
    }
  };

  const handleSaveComparison = async () => {
    if (selectedColleges.length < 2) return;
    const name = `Comparison ${new Date().toLocaleDateString()}`;
    try {
      await api.post('/compare/save', { name, college_ids: selectedColleges });
      toast({ title: 'Success', description: 'Comparison saved' });
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save', variant: 'destructive' });
    }
  };

  const handleLoadComparison = (comp) => {
    setSelectedColleges(comp.college_ids);
    navigate(`/compare?colleges=${comp.college_ids.join(',')}`);
    loadComparison(comp.college_ids);
  };

  const formatCurrency = (value) => {
    // Handle new format with estimated flag
    if (value && typeof value === 'object') {
      const val = value.value;
      const isEstimated = value.estimated;
      if (!val || val === 'N/A') return { display: 'N/A', estimated: false };
      return { 
        display: `$${val.toLocaleString()}`, 
        estimated: isEstimated 
      };
    }
    // Legacy format
    if (!value || value === 'N/A') return { display: 'N/A', estimated: false };
    return { display: `$${value.toLocaleString()}`, estimated: false };
  };

  const formatPercent = (value) => {
    // Handle new format with estimated flag
    if (value && typeof value === 'object') {
      const val = value.value;
      const isEstimated = value.estimated;
      if (!val || val === 'N/A') return { display: 'N/A', estimated: false };
      return { 
        display: `${val}%`, 
        estimated: isEstimated 
      };
    }
    // Legacy format
    if (!value || value === 'N/A') return { display: 'N/A', estimated: false };
    return { display: `${value}%`, estimated: false };
  };

  const formatValue = (value, prefix = '', suffix = '') => {
    // Handle new format with estimated flag
    if (value && typeof value === 'object') {
      const val = value.value;
      const isEstimated = value.estimated;
      if (!val || val === 'N/A') return { display: 'N/A', estimated: false };
      return { 
        display: `${prefix}${val}${suffix}`, 
        estimated: isEstimated 
      };
    }
    // Legacy format
    if (!value || value === 'N/A') return { display: 'N/A', estimated: false };
    return { display: `${prefix}${value}${suffix}`, estimated: false };
  };

  // Component to render a value with estimate indicator
  const ValueCell = ({ value, className = '' }) => {
    const formatted = typeof value === 'string' ? { display: value, estimated: false } : value;
    return (
      <span className={`${className} ${formatted.estimated ? 'text-amber-700' : ''}`}>
        {formatted.display}
        {formatted.estimated && (
          <span className="ml-1 text-xs text-amber-500" title="Estimated based on similar colleges">~</span>
        )}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">College Comparison</h1>
          <p className="text-gray-600">Compare up to 5 colleges side by side</p>
        </div>

        {/* Selected Colleges Bar */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center flex-wrap gap-3">
              <span className="font-medium text-gray-700">Comparing:</span>
              {selectedColleges.map(id => {
                const college = allColleges.find(c => c._id === id);
                return (
                  <Badge key={id} variant="secondary" className="px-3 py-1 flex items-center gap-2">
                    {college?.name || 'Unknown'}
                    <button onClick={() => handleRemoveCollege(id)} className="hover:text-red-500">
                      <XCircle className="h-4 w-4" />
                    </button>
                  </Badge>
                );
              })}
              {selectedColleges.length < 5 && (
                <Button variant="outline" size="sm" onClick={() => setShowSelector(!showSelector)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add College
                </Button>
              )}
              {selectedColleges.length >= 2 && (
                <Button variant="outline" size="sm" onClick={handleSaveComparison}>
                  <Save className="h-4 w-4 mr-1" />
                  Save
                </Button>
              )}
            </div>

            {/* College Selector with Search */}
            {showSelector && (
              <div className="mt-4 p-4 border rounded-lg bg-white">
                {/* Search Input */}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search universities by name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-10"
                    autoFocus
                    data-testid="college-search-input"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                
                {/* Filtered Results */}
                <div className="max-h-64 overflow-y-auto">
                  {(() => {
                    const filteredColleges = allColleges
                      .filter(c => !selectedColleges.includes(c._id))
                      .filter(c => 
                        searchQuery.trim() === '' || 
                        c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        (c.location && c.location.toLowerCase().includes(searchQuery.toLowerCase()))
                      );
                    
                    if (filteredColleges.length === 0) {
                      return (
                        <div className="text-center py-8 text-gray-500">
                          <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p>No universities found matching "{searchQuery}"</p>
                          <p className="text-sm mt-1">Try a different search term</p>
                        </div>
                      );
                    }
                    
                    return (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {filteredColleges.slice(0, 50).map(college => (
                          <button
                            key={college._id}
                            onClick={() => handleAddCollege(college._id)}
                            className="text-left p-3 hover:bg-emerald-50 hover:border-emerald-200 rounded-lg border border-transparent transition-colors group"
                            data-testid={`college-option-${college._id}`}
                          >
                            <div className="font-medium text-sm text-gray-900 group-hover:text-emerald-700">
                              {college.name}
                            </div>
                            {college.location && (
                              <div className="text-xs text-gray-500 mt-0.5">
                                {college.location}
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </div>
                
                {/* Results count */}
                {searchQuery && (
                  <div className="text-xs text-gray-400 mt-3 pt-3 border-t">
                    Showing {Math.min(50, allColleges.filter(c => !selectedColleges.includes(c._id) && c.name.toLowerCase().includes(searchQuery.toLowerCase())).length)} results
                    {allColleges.filter(c => !selectedColleges.includes(c._id) && c.name.toLowerCase().includes(searchQuery.toLowerCase())).length > 50 && ' (type more to narrow down)'}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Saved Comparisons */}
        {savedComparisons.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Saved Comparisons</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {savedComparisons.map((comp, idx) => (
                  <Button key={idx} variant="outline" size="sm" onClick={() => handleLoadComparison(comp)}>
                    {comp.college_names?.join(' vs ') || comp.name}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Comparison Table */}
        {comparison && comparison.colleges.length >= 2 ? (
          <div className="space-y-6">
            {/* Estimate Notice */}
            {comparison.has_estimates && (
              <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 px-4 py-2 rounded-lg">
                <span className="text-amber-500">~</span>
                <span>Values marked with ~ are estimates based on college type and selectivity tier</span>
              </div>
            )}

            {/* Basic Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="h-5 w-5" />
                  Basic Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-4 w-48">Metric</th>
                        {comparison.colleges.map(c => (
                          <th key={c.id} className="text-left py-2 px-4 font-semibold">{c.name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-2 px-4 text-gray-600">Location</td>
                        {comparison.colleges.map(c => <td key={c.id} className="py-2 px-4">{c.basic_info.location}</td>)}
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-4 text-gray-600">Type</td>
                        {comparison.colleges.map(c => <td key={c.id} className="py-2 px-4">{c.basic_info.type}</td>)}
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-4 text-gray-600">Enrollment</td>
                        {comparison.colleges.map(c => <td key={c.id} className="py-2 px-4">{c.basic_info.size?.toLocaleString() || 'N/A'}</td>)}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Admissions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-5 w-5" />
                  Admissions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-4 w-48">Metric</th>
                        {comparison.colleges.map(c => (
                          <th key={c.id} className="text-left py-2 px-4 font-semibold">{c.name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-2 px-4 text-gray-600">Acceptance Rate</td>
                        {comparison.colleges.map(c => {
                          const val = formatPercent(c.admissions.acceptance_rate);
                          const rawRate = c.admissions.acceptance_rate?.value || c.admissions.acceptance_rate;
                          return (
                            <td key={c.id} className="py-2 px-4">
                              <ValueCell 
                                value={val} 
                                className={rawRate && rawRate < 20 ? 'font-medium' : ''}
                              />
                            </td>
                          );
                        })}
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-4 text-gray-600">Average SAT</td>
                        {comparison.colleges.map(c => (
                          <td key={c.id} className="py-2 px-4">
                            <ValueCell value={formatValue(c.admissions.average_sat)} />
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-4 text-gray-600">Average GPA</td>
                        {comparison.colleges.map(c => (
                          <td key={c.id} className="py-2 px-4">
                            <ValueCell value={formatValue(c.admissions.average_gpa)} />
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-4 text-gray-600">ED Acceptance Rate</td>
                        {comparison.colleges.map(c => (
                          <td key={c.id} className="py-2 px-4">
                            <ValueCell value={formatPercent(c.admissions.ed_acceptance_rate)} />
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-4 text-gray-600">Test Optional</td>
                        {comparison.colleges.map(c => (
                          <td key={c.id} className="py-2 px-4">
                            {c.admissions.test_optional ? <CheckCircle className="h-5 w-5 text-green-500" /> : <XCircle className="h-5 w-5 text-gray-300" />}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Costs */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Costs & Financial Aid
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-4 w-48">Metric</th>
                        {comparison.colleges.map(c => (
                          <th key={c.id} className="text-left py-2 px-4 font-semibold">{c.name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-2 px-4 text-gray-600">Tuition & Fees</td>
                        {comparison.colleges.map(c => (
                          <td key={c.id} className="py-2 px-4">
                            <ValueCell value={formatCurrency(c.costs.tuition_and_fees)} />
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-4 text-gray-600">Room & Board</td>
                        {comparison.colleges.map(c => (
                          <td key={c.id} className="py-2 px-4">
                            <ValueCell value={formatCurrency(c.costs.room_and_board)} />
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b bg-gray-50">
                        <td className="py-2 px-4 text-gray-900 font-medium">Total Cost</td>
                        {comparison.colleges.map(c => (
                          <td key={c.id} className="py-2 px-4 font-semibold text-emerald-600">
                            <ValueCell value={formatCurrency(c.costs.total_cost)} />
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-4 text-gray-600">Average Aid</td>
                        {comparison.colleges.map(c => (
                          <td key={c.id} className="py-2 px-4">
                            <ValueCell value={formatCurrency(c.costs.average_aid)} />
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-4 text-gray-600">% Receiving Aid</td>
                        {comparison.colleges.map(c => (
                          <td key={c.id} className="py-2 px-4">
                            <ValueCell value={formatPercent(c.costs.percent_receiving_aid)} />
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Academics */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="h-5 w-5" />
                  Academics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-4 w-48">Metric</th>
                        {comparison.colleges.map(c => (
                          <th key={c.id} className="text-left py-2 px-4 font-semibold">{c.name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-2 px-4 text-gray-600">Student:Faculty Ratio</td>
                        {comparison.colleges.map(c => (
                          <td key={c.id} className="py-2 px-4">
                            <ValueCell value={formatValue(c.academics.student_faculty_ratio)} />
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-4 text-gray-600">Classes Under 20</td>
                        {comparison.colleges.map(c => (
                          <td key={c.id} className="py-2 px-4">
                            <ValueCell value={formatPercent(c.academics.class_size_under_20)} />
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Outcomes */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Outcomes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-4 w-48">Metric</th>
                        {comparison.colleges.map(c => (
                          <th key={c.id} className="text-left py-2 px-4 font-semibold">{c.name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-2 px-4 text-gray-600">Graduation Rate</td>
                        {comparison.colleges.map(c => (
                          <td key={c.id} className="py-2 px-4">
                            <ValueCell value={formatPercent(c.outcomes.graduation_rate)} />
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-4 text-gray-600">Retention Rate</td>
                        {comparison.colleges.map(c => (
                          <td key={c.id} className="py-2 px-4">
                            <ValueCell value={formatPercent(c.outcomes.retention_rate)} />
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-4 text-gray-600">Employment Rate</td>
                        {comparison.colleges.map(c => (
                          <td key={c.id} className="py-2 px-4">
                            <ValueCell value={formatPercent(c.outcomes.employment_rate)} />
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-4 text-gray-600">Median Salary (10 yr)</td>
                        {comparison.colleges.map(c => (
                          <td key={c.id} className="py-2 px-4">
                            <ValueCell value={formatCurrency(c.outcomes.median_salary)} />
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-4 text-gray-600">Grad School Rate</td>
                        {comparison.colleges.map(c => (
                          <td key={c.id} className="py-2 px-4">
                            <ValueCell value={formatPercent(c.outcomes.grad_school_rate)} />
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <GitCompare className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Compare Colleges</h3>
              <p className="text-gray-500 mb-4">Select at least 2 colleges to compare them side by side</p>
              <Button onClick={() => setShowSelector(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Colleges
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CollegeCompare;
